import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import * as L from 'leaflet';
import { Subscription } from 'rxjs';
import { SemControlComponent } from '../sem-control/sem-control.component';
import {
  GeoCoordinateSystem,
  fromWgs84,
  haversineMeters,
  toNumber,
  toWgs84
} from '../../../services/geo/coordinate-transform';

/** A row of a marker popup, read off the bound item. */
interface GeoMapPopupRow {
  name: any;
  value: any;
  dataType: string;
}

/** What a bound item says in its bubble, resolved. Built the same way for a pin and for a shape. */
interface GeoMapPopupContent {
  title: any;
  description: any;
  rows: GeoMapPopupRow[];
}

/**
 * One marker the map draws. The coordinates stay property paths - a dragged marker writes its new
 * position back to them - while the popup is already resolved values.
 */
interface GeoMapPin {
  Latitude: string;
  Longitude: string;
  Popup: GeoMapPopupContent;
}

/**
 * The base map and the view it falls back to. Fixed rather than configurable: every deployment of this
 * control draws the same OpenStreetMap tiles over the same country, and a form that got either wrong
 * showed a blank or misplaced map with nothing on screen to explain it.
 */
const TILE_LAYER_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_LAYER_ATTRIBUTION = '&copy; OpenStreetMap contributors';

/** Esri World Imagery: the free, key-less satellite tile source paired with the OSM layer above. */
const SATELLITE_TILE_LAYER_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE_TILE_LAYER_ATTRIBUTION = 'Tiles &copy; Esri';

const DEFAULT_LATITUDE = 46.1512;
const DEFAULT_LONGITUDE = 14.9955;

/** Heroicons "map" outline, 24x24 - the standard-view button. */
const MAP_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
  '<path stroke-linecap="round" stroke-linejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.804a1.125 1.125 0 00-1.006 0L3.622 6.24C3.24 6.43 3 6.82 3 7.246v13.44c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z"/></svg>';

/** Heroicons "globe-alt" outline, 24x24 - the satellite-view button. */
const SATELLITE_ICON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
  '<path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A8.959 8.959 0 013 12c0-.778.099-1.533.284-2.253"/></svg>';

/** How long the distance label takes to fade once the pointer leaves its line. Matches the css. */
const DISTANCE_FADE_MS = 420;

/** A popup row declared in the settings, read off the pin's item rather than from a bound collection. */
interface GeoMapPopupProperty {
  Label: string;
  Binding: string;
  DataType: string;
}

/** The popup mappings a bound item carries, whether it is drawn as a pin or as a shape. */
interface GeoMapPopupBinding {
  TitleBinding: string;
  DescriptionBinding: string;
  PopupProperties: GeoMapPopupProperty[];
  PopupFieldsBinding: string;
  PopupNameBinding: string;
  PopupValueBinding: string;
  PopupDataTypeBinding: string;
  PopupSortOrderBinding: string;
}

interface GeoMapPointBinding extends GeoMapPopupBinding {
  ObjectBinding: string;
  ListBinding: string;
  IdBinding: string;
  LatitudeBinding: string;
  LongitudeBinding: string;
}

interface GeoMapShapeBinding extends GeoMapPopupBinding {
  ObjectBinding: string;
  ListBinding: string;
  GeometryBinding: string;
  LabelBinding: string;
  HighlightedBinding: string;
}

/** Which basemap tile layer the map starts from. The end user can still switch live on top of this. */
type GeoBaseLayer = 'Standard' | 'Satellite';

/** Settings as serialized by SemGeoMapControl on the server. */
interface GeoMapSettings {
  Title: string;
  /** A [Flags] enum, serialized by name: "MultiPin, Connections". */
  EnabledFeatures: string | number;
  SinglePinBinding: GeoMapPointBinding;
  MultiPinBinding: GeoMapPointBinding;
  ShapeBinding: GeoMapShapeBinding;
  CoordinateSystem: GeoCoordinateSystem;

  DefaultZoom: number;
  MaxZoom: number;
  PinZoom: number;
  MapHeight: number;
  ShapeColor: string;
  DefaultBaseLayer: GeoBaseLayer;

  AllowEdit: boolean;
  ReadOnly: boolean;
}

/** A rendered marker and the paths its position writes back to. */
interface GeoMapMarker {
  marker: L.Marker;
  latitudeBinding: string;
  longitudeBinding: string;
  sourceIndex: number;
}

/**
 * Generic map control. Everything it draws belongs to the current record: the settings only name the
 * property to bind and how each bound item maps to a pin or to a shape, and every value is read and
 * written through the platform's DynamicFieldsManagerService. That keeps the control free of any model
 * specific knowledge and usable from any module.
 */
@Component({
  selector: 'app-sem-geo-map',
  templateUrl: './sem-geo-map.component.html',
  styleUrls: ['./sem-geo-map.component.css'],
  standalone: false
})
export class SemGeoMapComponent extends SemControlComponent
  implements OnInit, AfterViewInit, DoCheck, OnDestroy {

  @Input() settingsJson: string;

  @ViewChild('mapHolder', { static: false }) mapHolder: ElementRef;

  settings: GeoMapSettings;
  /** Set when the settings json cannot be parsed, so a misconfigured form says so instead of staying blank. */
  configurationError: string = '';
  /** Set when the bound geometry could not be used. */
  dataError: string = '';

  private map: L.Map;
  private markerLayer: L.LayerGroup;
  private shapeLayer: L.LayerGroup;
  private lineLayer: L.LayerGroup;

  private standardTileLayer: L.TileLayer;
  private satelliteTileLayer: L.TileLayer;
  /** The live view. Starts from settings.DefaultBaseLayer but isn't persisted - a fresh load always
   *  starts from the admin-configured default again. */
  private activeBaseLayer: GeoBaseLayer;
  private baseLayerButtons: Partial<Record<GeoBaseLayer, HTMLElement>> = {};

  private renderedMarkers: GeoMapMarker[] = [];

  private lastEditable: boolean = null;
  private lastModel: any = null;
  /** Resolved once from the host's computed style - see accentColor. */
  private resolvedAccent: string = null;
  private editModeEnabled = false;
  private selectedPinIndex: number | null = null;
  readonly popupDataTypes = ['Text', 'Number', 'Date', 'Boolean'];
  /** Settings the configuration needs but does not have. Non-empty means there is nothing to draw. */
  private missingBindings: string[] = [];
  /** False while redrawing after a drag, so the viewport is left where the user put it. */
  private fitOnRender: boolean = true;
  /** What the current render pass has put on the map, shapes and pins together. */
  private renderBounds: L.LatLngBounds = null;
  private subscriptions: Subscription[] = [];

  ngOnInit() {
    super.ngOnInit();

    this.settings = this.parseSettings();
    this.missingBindings = this.findMissingBindings();

    if (this.missingBindings.length > 0) {
      console.warn(
        'sem-geo-map: the map has nothing to draw until these settings are filled in: ' +
        this.missingBindings.join(', ')
      );
    }

    this.subscriptions.push(
      this.restProviderService.modelValueChanged.subscribe(() => {
        // The model is filled in asynchronously, after the form has been rendered.
        this.refresh();
      })
    );
  }

  ngAfterViewInit() {
    if (this.configurationError) {
      return;
    }

    // Let the surrounding layout settle before Leaflet measures its container.
    setTimeout(() => this.initMap(), 0);
  }

  /**
   * Edit mode is a plain flag on DynamicFieldsManagerService with no event to subscribe to, and the
   * model input can be assigned late - both are picked up during change detection.
   */
  ngDoCheck() {
    if (this.model !== this.lastModel) {
      this.lastModel = this.model;
      this.refresh();
    }

    if (!this.canEditMap() && this.editModeEnabled) {
      this.editModeEnabled = false;
      this.selectedPinIndex = null;
    }

    const editable = this.isEditable();

    if (editable !== this.lastEditable) {
      this.lastEditable = editable;
      this.applyEditability();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];

    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    // wg-panel-manager.directive.ts reparents our ancestor .dynamic-sidebar-container straight to
    // <body> (so its position:fixed resolves against the viewport). That breaks Angular's own
    // cleanup on route change: the renderer still targets the container's original parent, so its
    // removeChild call silently no-ops and the panel is orphaned under <body> - still visible,
    // showing stale content, until a manual page refresh. See sem-activity-trail's ngOnDestroy for
    // the same fix - this component's ngOnDestroy reliably fires on route change regardless of
    // where the DOM node physically sits, so remove the reparented container ourselves.
    this.control.nativeElement.closest('.dynamic-sidebar-container')?.remove();
  }

  get showEditModeToggle(): boolean {
    return this.canEditMap();
  }

  get isEditModeEnabled(): boolean {
    return this.editModeEnabled;
  }

  get showPinEditor(): boolean {
    return this.isEditable() && this.selectedPinIndex !== null && !!this.selectedPinBinding();
  }

  /** The title is only editable once the form definition says where to keep it. */
  get showPinTitle(): boolean {
    const binding = this.selectedPinBinding();

    return !!(binding && binding.TitleBinding);
  }

  /** Same for the popup rows. */
  get showPopupFields(): boolean {
    return !!this.selectedPopupFieldsPath();
  }

  get selectedPopupFields(): any[] {
    const path = this.selectedPopupFieldsPath();

    return path ? this.getBoundList(path) || [] : [];
  }

  get selectedPinTitle(): string {
    const binding = this.selectedPinBinding();

    if (!binding) {
      return '';
    }

    return String(
      this.valueAtBinding(this.selectedPinPrefix(), binding.TitleBinding) ||
      'Pin ' + (this.selectedPinIndex + 1)
    );
  }

  get selectedPinTitleValue(): string {
    const binding = this.selectedPinBinding();
    const value = binding ? this.valueAtBinding(this.selectedPinPrefix(), binding.TitleBinding) : null;

    return value === null || typeof value === 'undefined' ? '' : String(value);
  }

  /** The description is only editable once the form definition says where to keep it. */
  get showPinDescription(): boolean {
    const binding = this.selectedPinBinding();

    return !!(binding && binding.DescriptionBinding);
  }

  get selectedPinDescriptionValue(): string {
    const binding = this.selectedPinBinding();
    const value = binding ? this.valueAtBinding(this.selectedPinPrefix(), binding.DescriptionBinding) : null;

    return value === null || typeof value === 'undefined' ? '' : String(value);
  }

  popupFieldValue(index: number, part: 'name' | 'value' | 'dataType'): string {
    const path = this.selectedPopupFieldsPath();
    const propertyBinding = this.popupPartBinding(part);

    if (!path || !propertyBinding) {
      return '';
    }

    const value = this.valueAtBinding(path + '[' + index + ']', propertyBinding);

    return value === null || typeof value === 'undefined' ? '' : String(value);
  }

  updateSelectedPinTitle(event: Event) {
    const binding = this.selectedPinBinding();
    const input = event.target as HTMLInputElement;

    if (!binding || !input) {
      return;
    }

    if (!binding.TitleBinding) {
      console.error('sem-geo-map: cannot update the pin title because TitleBinding is not configured.');
      return;
    }

    this.assignBoundValue(this.combineBinding(this.selectedPinPrefix(), binding.TitleBinding), input.value);
    this.dfms.valueChanged({ binding: this.pinRoot(), action: 'update-map-pin-title' });
    this.refresh(false);
  }

  updateSelectedPinDescription(event: Event) {
    const binding = this.selectedPinBinding();
    const input = event.target as HTMLTextAreaElement;

    if (!binding || !input) {
      return;
    }

    if (!binding.DescriptionBinding) {
      console.error(
        'sem-geo-map: cannot update the pin description because DescriptionBinding is not configured.'
      );
      return;
    }

    this.assignBoundValue(
      this.combineBinding(this.selectedPinPrefix(), binding.DescriptionBinding),
      input.value
    );
    this.dfms.valueChanged({ binding: this.pinRoot(), action: 'update-map-pin-description' });
    this.refresh(false);
  }

  toggleEditMode() {
    if (!this.canEditMap()) {
      return;
    }

    this.editModeEnabled = !this.editModeEnabled;

    if (!this.editModeEnabled) {
      this.selectedPinIndex = null;
    }

    this.applyEditability();
  }

  isEditable(): boolean {
    return this.editModeEnabled && this.canEditMap();
  }

  selectPin(index: number) {
    if (!this.isEditable()) {
      return;
    }

    this.selectedPinIndex = index;
  }

  closePinEditor() {
    this.selectedPinIndex = null;
  }

  addPopupField() {
    const path = this.selectedPopupFieldsPath();
    const binding = this.selectedPinBinding();

    if (!path || !binding) {
      console.error('sem-geo-map: cannot add a popup field because PopupFieldsBinding is not configured.');
      return;
    }

    const fields = this.getBoundList(path) || [];
    const field: any = {};
    this.dfms.assign(field, binding.PopupNameBinding, '', true);
    this.dfms.assign(field, binding.PopupValueBinding, '', true);
    this.dfms.assign(field, binding.PopupDataTypeBinding, 'Text', true);
    this.dfms.assign(field, binding.PopupSortOrderBinding, fields.length + 1, true);
    this.savePopupFields(path, fields.concat([field]), 'add-map-popup-field');
  }

  updatePopupField(index: number, part: 'name' | 'value' | 'dataType', event: Event) {
    const path = this.selectedPopupFieldsPath();
    const propertyBinding = this.popupPartBinding(part);
    const input = event.target as HTMLInputElement | HTMLSelectElement;

    if (!path || !propertyBinding || !input) {
      return;
    }

    this.assignBoundValue(this.combineBinding(path + '[' + index + ']', propertyBinding), input.value);
    this.notifyPopupFieldsChanged(path, 'update-map-popup-field');
    this.refresh(false);
  }

  removePopupField(index: number) {
    const path = this.selectedPopupFieldsPath();
    const fields = path ? this.getBoundList(path) : null;

    if (!path || fields === null || index < 0 || index >= fields.length) {
      return;
    }

    this.savePopupFields(path, fields.filter((_, fieldIndex) => fieldIndex !== index), 'remove-map-popup-field');
  }

  private canEditMap(): boolean {
    if (!this.settings || !this.settings.AllowEdit || this.settings.ReadOnly) {
      return false;
    }

    // Shape geometry is supplied by the module, the user never draws it - so a map that binds nothing
    // but shapes has nothing to edit.
    if (!this.hasPinBinding()) {
      return false;
    }

    if (this.missingBindings.length > 0 || !this.model) {
      return false;
    }

    if (this.dfms && this.dfms.isFormReadOnly === true) {
      return false;
    }

    return !this.checkForDisabled(this.getAttribute('name'));
  }

  get mapHeightPx(): string {
    return (this.settings && this.settings.MapHeight > 0 ? this.settings.MapHeight : 400) + 'px';
  }

  /** Shown while editing, so it is discoverable that pins can be moved at all. */
  get editHint(): string {
    if (!this.isEditable() || this.renderedMarkers.length === 0) {
      return '';
    }

    return this.translateOr('WebGalis_GeoMap_DragToMove', 'Točke lahko premikate z vlečenjem.');
  }

  /**
   * Shown while the form is editable, so a half configured control says so instead of drawing an empty
   * map. Hidden on read only forms, where an end user can do nothing about it anyway.
   */
  get bindingWarning(): string {
    if (this.missingBindings.length === 0 || (this.dfms && this.dfms.isFormReadOnly === true)) {
      return '';
    }

    return this.translateOr('WebGalis_GeoMap_MissingBindings', 'Manjkajoče nastavitve zemljevida') +
      ': ' + this.missingBindings.join(', ');
  }

  // ---------------------------------------------------------------- settings

  private parseSettings(): GeoMapSettings {
    const defaults: GeoMapSettings = {
      Title: '',
      EnabledFeatures: '',
      SinglePinBinding: null,
      MultiPinBinding: null,
      ShapeBinding: null,
      CoordinateSystem: 'Wgs84',
      DefaultZoom: 8,
      MaxZoom: 19,
      PinZoom: 16,
      MapHeight: 400,
      // Empty means "follow the platform accent" - see pinColor.
      ShapeColor: '',
      DefaultBaseLayer: 'Standard',
      AllowEdit: true,
      ReadOnly: false
    };

    if (!this.settingsJson) {
      this.configurationError = this.translateOr(
        'WebGalis_GeoMap_NoSettings',
        'Kontrola zemljevida nima nastavitev.'
      );

      return defaults;
    }

    try {
      const settings = Object.assign(defaults, JSON.parse(this.settingsJson)) as GeoMapSettings;

      settings.CoordinateSystem = this.normalizeCoordinateSystem(settings.CoordinateSystem);
      settings.DefaultBaseLayer = this.normalizeBaseLayer(settings.DefaultBaseLayer);

      return settings;
    } catch (e) {
      console.error('sem-geo-map: cannot parse settings json', e);

      this.configurationError = this.translateOr(
        'WebGalis_GeoMap_InvalidSettings',
        'Nastavitve kontrole zemljevida niso veljaven json.'
      );

      return defaults;
    }
  }

  private normalizeCoordinateSystem(value: any): GeoCoordinateSystem {
    if (typeof value === 'number') {
      return value === 1 ? 'D96Tm' : 'Wgs84';
    }

    return String(value || '').toLowerCase() === 'd96tm' ? 'D96Tm' : 'Wgs84';
  }

  private normalizeBaseLayer(value: any): GeoBaseLayer {
    return String(value || '').toLowerCase() === 'satellite' ? 'Satellite' : 'Standard';
  }

  /**
   * EnabledFeatures is a [Flags] enum which the server serializes by name - "MultiPin, Connections" -
   * so a name list and a raw bit mask are both accepted.
   */
  private hasFeature(name: string, bit: number): boolean {
    const value = this.settings ? this.settings.EnabledFeatures : null;

    if (typeof value === 'number') {
      return (value & bit) === bit;
    }

    return String(value || '')
      .split(',')
      .map(part => part.trim().toLowerCase())
      .indexOf(name.toLowerCase()) >= 0;
  }

  private hasPinBinding(): boolean {
    return !!this.pinBinding();
  }

  /**
   * Whether a feature may draw. The switches act as a filter over what is bound, so turning Shapes on
   * takes the pins off the map even though their bindings are still configured.
   *
   * A map that names no feature at all is left unfiltered: definitions written before the switches
   * existed carry nothing here, and blanking them would be a regression rather than a setting.
   */
  private isFeatureEnabled(name: string, bit: number): boolean {
    return !this.hasAnyFeature() || this.hasFeature(name, bit);
  }

  private hasAnyFeature(): boolean {
    const value = this.settings ? this.settings.EnabledFeatures : null;

    if (typeof value === 'number') {
      return value > 0;
    }

    return String(value || '').split(',').some(part => {
      const name = part.trim().toLowerCase();

      return name !== '' && name !== 'none';
    });
  }

  /**
   * A pin can only be created where its coordinates sit directly on the bound item. A nested mapping
   * such as `Location.D96_X` would mean inventing the object in between, and the map cannot know
   * whether that is a new record, an existing one to look up, or a relation the module forbids - so a
   * map click is refused rather than appending a half built item the server then has to make sense of.
   * Reading, dragging and typing coordinates keep working: those items already exist.
   */
  private canCreatePins(binding: GeoMapPointBinding): boolean {
    return (binding.LatitudeBinding || '').indexOf('.') < 0 &&
      (binding.LongitudeBinding || '').indexOf('.') < 0;
  }

  /** What the current configuration is missing before the map can show anything. */
  private findMissingBindings(): string[] {
    const missing: string[] = [];

    if (!this.settings) {
      return missing;
    }

    const multi = this.settings.MultiPinBinding;
    const single = this.settings.SinglePinBinding;
    const shape = this.settings.ShapeBinding;
    let configured = false;

    // A mode the switches have turned off is not validated: its bindings are configuration for a
    // feature this map does not draw.
    if (multi && multi.ListBinding && this.isFeatureEnabled('MultiPin', 2)) {
      configured = true;

      if (!multi.LatitudeBinding) {
        missing.push('MultiPinBinding.LatitudeBinding');
      }

      if (!multi.LongitudeBinding) {
        missing.push('MultiPinBinding.LongitudeBinding');
      }
    }

    if (this.isSinglePinConfigured(single) && this.isFeatureEnabled('SinglePin', 1)) {
      configured = true;

      if (!single.LatitudeBinding) {
        missing.push('SinglePinBinding.LatitudeBinding');
      }

      if (!single.LongitudeBinding) {
        missing.push('SinglePinBinding.LongitudeBinding');
      }
    }

    if (this.isShapeConfigured(shape) && this.isFeatureEnabled('Shapes', 4)) {
      configured = true;

      if (!shape.GeometryBinding) {
        missing.push('ShapeBinding.GeometryBinding');
      }
    }

    // Nothing is bound at all - name the binding a pin map needs, since that is what most forms want.
    if (!configured) {
      missing.push('MultiPinBinding.ListBinding');
    }

    return missing;
  }

  // --------------------------------------------------------------------- map

  private initMap() {
    if (this.map || !this.mapHolder) {
      return;
    }

    this.map = L.map(this.mapHolder.nativeElement, {
      center: [DEFAULT_LATITUDE, DEFAULT_LONGITUDE],
      zoom: this.settings.DefaultZoom,
      // Wheel zoom would swallow page scrolling on long forms; the zoom buttons stay available.
      scrollWheelZoom: false
    });

    this.standardTileLayer = L.tileLayer(TILE_LAYER_URL, {
      maxZoom: this.settings.MaxZoom,
      attribution: TILE_LAYER_ATTRIBUTION
    });

    this.satelliteTileLayer = L.tileLayer(SATELLITE_TILE_LAYER_URL, {
      maxZoom: this.settings.MaxZoom,
      attribution: SATELLITE_TILE_LAYER_ATTRIBUTION
    });

    this.activeBaseLayer = this.settings.DefaultBaseLayer;
    this.tileLayerFor(this.activeBaseLayer).addTo(this.map);
    this.addBaseLayerControl();

    // The map is read for distance as much as for position, so it carries a scale. Metric only -
    // every coordinate system this control supports is metric.
    L.control.scale({ metric: true, imperial: false, position: 'bottomleft' }).addTo(this.map);

    this.shapeLayer = L.layerGroup().addTo(this.map);
    this.lineLayer = L.layerGroup().addTo(this.map);
    this.markerLayer = L.layerGroup().addTo(this.map);

    this.map.on('click', event => this.addPinAt(event.latlng));

    // The map is often created while its tab is still hidden and measures as zero sized.
    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 200);

    this.refresh();
  }

  private tileLayerFor(layer: GeoBaseLayer): L.TileLayer {
    return layer === 'Satellite' ? this.satelliteTileLayer : this.standardTileLayer;
  }

  /**
   * Switches the live view only - not persisted anywhere, so the next time this map loads it starts
   * from settings.DefaultBaseLayer again, same as today.
   */
  private setBaseLayer(layer: GeoBaseLayer) {
    if (!this.map || layer === this.activeBaseLayer) {
      return;
    }

    this.map.removeLayer(this.tileLayerFor(this.activeBaseLayer));
    this.activeBaseLayer = layer;
    this.tileLayerFor(layer).addTo(this.map);

    this.updateBaseLayerButtons();
  }

  /**
   * A small leaflet-bar control, styled like the built-in zoom control, offered top-right so it does
   * not collide with the default top-left zoom control or the bottom-left scale control.
   */
  private addBaseLayerControl() {
    const buildButton = (layer: GeoBaseLayer, label: string, svg: string): HTMLElement => {
      const button = L.DomUtil.create('a', 'sem-geo-map-baselayer-btn') as HTMLAnchorElement;
      button.href = '#';
      button.title = label;
      button.setAttribute('aria-label', label);
      button.innerHTML = svg;

      L.DomEvent.on(button, 'click', (event: Event) => {
        L.DomEvent.stop(event);
        this.setBaseLayer(layer);
      });

      this.baseLayerButtons[layer] = button;

      return button;
    };

    const BaseLayerControl = L.Control.extend({
      onAdd: (): HTMLElement => {
        // Deliberately no `leaflet-bar` class: this control is fully self-styled below, and
        // `leaflet-bar`'s own CSS (first/last-child corner radii, especially the .leaflet-touch
        // variant) out-specifies a same-weight override and fights the circular button shape.
        const container = L.DomUtil.create('div', 'leaflet-control sem-geo-map-baselayer-control');

        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        container.appendChild(buildButton('Standard', 'Standard map', MAP_ICON_SVG));
        container.appendChild(buildButton('Satellite', 'Satellite imagery', SATELLITE_ICON_SVG));

        return container;
      }
    });

    new BaseLayerControl({ position: 'topright' }).addTo(this.map);
    this.updateBaseLayerButtons();
  }

  private updateBaseLayerButtons() {
    (Object.keys(this.baseLayerButtons) as GeoBaseLayer[]).forEach(layer => {
      const button = this.baseLayerButtons[layer];

      if (button) {
        button.classList.toggle('sem-geo-map-baselayer-btn-active', layer === this.activeBaseLayer);
      }
    });
  }

  /**
   * Reads everything from the record again and redraws. `fit` is false after a drag, so the map does
   * not jump around under the cursor.
   */
  private refresh(fit: boolean = true) {
    if (!this.map || !this.settings || !this.model) {
      return;
    }

    this.fitOnRender = fit;
    this.markerLayer.clearLayers();
    this.lineLayer.clearLayers();
    this.renderedMarkers = [];
    this.renderBounds = null;

    const hasBoundShapes = this.isShapeConfigured(this.settings.ShapeBinding) &&
      this.isFeatureEnabled('Shapes', 4);

    // Shapes are additive - they share the map with whichever pins are enabled.
    if (hasBoundShapes) {
      this.renderShapes();
    }

    this.renderPins();
    this.applyViewport();
    this.applyEditability();
  }

  /**
   * Frames everything the pass drew, once. Shapes and pins each used to fit the map themselves, which
   * meant whichever ran last won - a shapes-only map was framed on its parcel and then thrown back to
   * the default view by the pin pass finding nothing.
   */
  private applyViewport() {
    if (!this.fitOnRender) {
      return;
    }

    if (!this.renderBounds || !this.renderBounds.isValid()) {
      this.fitToDefault();
      return;
    }

    // A lone pin has no extent to fit, so it is framed at the configured pin zoom instead.
    if (this.renderBounds.getNorthEast().equals(this.renderBounds.getSouthWest())) {
      this.map.setView(this.renderBounds.getCenter(), this.settings.PinZoom);
      return;
    }

    this.map.fitBounds(this.renderBounds, { padding: [20, 20], maxZoom: this.settings.MaxZoom });
  }

  private extendBounds(value: L.LatLngBounds | L.LatLng) {
    const bounds = value instanceof L.LatLngBounds ? value : L.latLngBounds(value, value);

    this.renderBounds = this.renderBounds ? this.renderBounds.extend(bounds) : bounds;
  }

  private applyEditability() {
    if (!this.map) {
      return;
    }

    const editable = this.isEditable();

    this.renderedMarkers.forEach(rendered => {
      if (!rendered.marker.dragging) {
        return;
      }

      if (editable) {
        rendered.marker.dragging.enable();
      } else {
        rendered.marker.dragging.disable();
      }
    });
  }

  // ------------------------------------------------------------------- pins

  private renderPins() {
    const positions: L.LatLng[] = [];

    this.pinsForRender().forEach((pin, index) => {
      const latitude = this.dfms.getValue(this.model, pin.Latitude);
      const longitude = this.dfms.getValue(this.model, pin.Longitude);
      const converted = toWgs84(latitude, longitude, this.settings.CoordinateSystem);

      // One unusable item must not take the whole map down, but it has to say so: a silently missing
      // pin looks exactly like a wrong binding, an unprojected property and a swapped axis pair.
      if (!converted || !this.isRenderable(converted.lat, converted.lng)) {
        console.warn(
          'sem-geo-map: skipped pin ' + index + ' - ' +
          pin.Latitude + ' = ' + JSON.stringify(latitude) + ', ' +
          pin.Longitude + ' = ' + JSON.stringify(longitude) +
          ' is not a usable ' + this.settings.CoordinateSystem + ' position' +
          (converted
            ? ' (it converts to ' + converted.lat + ', ' + converted.lng + ').'
            : '.')
        );
        return;
      }

      const position = L.latLng(converted.lat, converted.lng);

      this.addMarker(position, pin, index);
      this.extendBounds(position);
      positions.push(position);
    });

    if (this.hasFeature('Connections', 8) && positions.length > 1) {
      this.renderConnectingLine(positions);
    }
  }

  /**
   * Marker data is bound to a model collection. Each configured relative mapping becomes an absolute
   * DynamicFieldsManager path, which marker rendering and write-back then share.
   */
  private pinsForRender(): GeoMapPin[] {
    const binding = this.pinBinding();

    if (!binding) {
      return [];
    }

    if (!binding.ListBinding) {
      return this.singlePinForRender(binding);
    }

    const items = this.getBoundList(binding.ListBinding);

    if (items === null) {
      if (this.isModelLoading()) {
        return [];
      }

      console.error(
        'sem-geo-map: multi-pin binding ' + binding.ListBinding + ' must resolve to a list.',
        this.dfms.getValue(this.model, binding.ListBinding),
        'model keys:', Object.keys(this.model || {})
      );
      return [];
    }

    if (!binding.LatitudeBinding || !binding.LongitudeBinding) {
      console.error(
        'sem-geo-map: multi-pin binding ' + binding.ListBinding +
        ' requires LatitudeBinding and LongitudeBinding.'
      );
      return [];
    }

    return items.map((_, index) => {
      const prefix = binding.ListBinding + '[' + index + ']';

      return {
        Latitude: this.combineBinding(prefix, binding.LatitudeBinding),
        Longitude: this.combineBinding(prefix, binding.LongitudeBinding),
        Popup: this.popupContentAt(prefix, binding)
      };
    });
  }

  private singlePinForRender(binding: GeoMapPointBinding): GeoMapPin[] {
    if (!binding.LatitudeBinding || !binding.LongitudeBinding) {
      console.error(
        'sem-geo-map: single-pin binding ' + (binding.ObjectBinding || 'on the form model') +
        ' requires LatitudeBinding and LongitudeBinding.'
      );
      return [];
    }

    return [{
      Latitude: this.combineBinding(binding.ObjectBinding, binding.LatitudeBinding),
      Longitude: this.combineBinding(binding.ObjectBinding, binding.LongitudeBinding),
      Popup: this.popupContentAt(binding.ObjectBinding, binding)
    }];
  }

  /** Everything one item says in its bubble: the headline, the note and the rows, all resolved. */
  private popupContentAt(prefix: string, binding: GeoMapPopupBinding): GeoMapPopupContent {
    return {
      title: this.valueAtBinding(prefix, binding.TitleBinding),
      description: this.valueAtBinding(prefix, binding.DescriptionBinding),
      rows: this.popupRowsAt(prefix, binding)
    };
  }

  /**
   * The popup rows of one bound item: first the ones the settings map off the item's own properties,
   * then the ones its popup collection carries. A record can use either, both or neither.
   */
  private popupRowsAt(prefix: string, binding: GeoMapPopupBinding): GeoMapPopupRow[] {
    return this.mappedPopupRows(prefix, binding).concat(this.collectedPopupRows(prefix, binding));
  }

  /**
   * Rows read straight off the pin's item. This is how a record that carries no popup collection - a
   * location, a parcel - still gets a popup, without growing a table just to describe itself.
   */
  private mappedPopupRows(prefix: string, binding: GeoMapPopupBinding): GeoMapPopupRow[] {
    return this.asArray(binding.PopupProperties)
      .filter(property => property && property.Binding)
      .map(property => ({
        name: property.Label ? this.translateOr(property.Label, property.Label) : '',
        value: this.valueAtBinding(prefix, property.Binding),
        dataType: property.DataType
      }));
  }

  /** Rows the item's own popup collection carries, in the order that list has them. */
  private collectedPopupRows(prefix: string, binding: GeoMapPopupBinding): GeoMapPopupRow[] {
    const popupPath = this.combineBinding(prefix, binding.PopupFieldsBinding);

    if (!popupPath) {
      return [];
    }

    const fields = this.getBoundList(popupPath);

    if (fields === null) {
      console.error('sem-geo-map: popup binding ' + popupPath + ' must resolve to a list.');
      return [];
    }

    return fields.map((_, index) => {
      const popupPrefix = popupPath + '[' + index + ']';

      return {
        name: this.valueAtBinding(popupPrefix, binding.PopupNameBinding),
        value: this.valueAtBinding(popupPrefix, binding.PopupValueBinding),
        dataType: this.valueAtBinding(popupPrefix, binding.PopupDataTypeBinding)
      };
    });
  }

  /**
   * Joins an item path and a mapping. An empty prefix means the mapping is already absolute - it is
   * read straight off the form model, which is what a single-pin map bound to its own record does.
   */
  private combineBinding(prefix: string, relative: string): string {
    if (!relative) {
      return '';
    }

    return prefix ? prefix + '.' + relative : relative;
  }

  private valueAtBinding(prefix: string, relative: string): any {
    const path = this.combineBinding(prefix, relative);

    return path ? this.dfms.getValue(this.model, path) : null;
  }

  /**
   * Dynamic-form responses can serialize an IList either as a JavaScript array or as the
   * Newtonsoft {$type, $values} wrapper. Keep the bound model in the array form so subsequent
   * coordinate paths, edits, and saves all use one representation.
   */
  private getBoundList(binding: string): any[] | null {
    const value = this.dfms.getValue(this.model, binding);

    if (Array.isArray(value)) {
      return value;
    }

    if (value && typeof value === 'object' && Array.isArray(value.$values)) {
      this.assignBoundValue(binding, value.$values);
      return value.$values;
    }

    return null;
  }

  /**
   * The binding the pins come from: the bound list, or the single bound object. Everything the editor
   * reads and writes hangs off this, so both kinds of map edit the same way.
   */
  private pinBinding(): GeoMapPointBinding | null {
    if (!this.settings) {
      return null;
    }

    const multi = this.settings.MultiPinBinding;

    if (multi && multi.ListBinding && this.isFeatureEnabled('MultiPin', 2)) {
      return multi;
    }

    return this.isSinglePinConfigured(this.settings.SinglePinBinding) &&
      this.isFeatureEnabled('SinglePin', 1)
      ? this.settings.SinglePinBinding
      : null;
  }

  /**
   * A single-pin map is configured as soon as it names its coordinates. ObjectBinding is optional:
   * without one the mappings are read off the form model itself, which is the shape a record that
   * carries its own position has - a location form showing that location.
   */
  private isSinglePinConfigured(binding: GeoMapPointBinding): boolean {
    return !!binding &&
      !!(binding.ObjectBinding || binding.LatitudeBinding || binding.LongitudeBinding);
  }

  /**
   * Same for shapes: naming the geometry is enough. Without an object or a list the mapping is read
   * off the form model, which is how a record that carries its own geometry binds - a location and
   * its parcel.
   */
  private isShapeConfigured(binding: GeoMapShapeBinding): boolean {
    return !!binding && !!(binding.ObjectBinding || binding.ListBinding || binding.GeometryBinding);
  }

  /** The property the pins are bound to. */
  private pinRoot(): string {
    const binding = this.pinBinding();

    return binding ? binding.ListBinding || binding.ObjectBinding : '';
  }

  private selectedPinBinding(): GeoMapPointBinding | null {
    if (this.selectedPinIndex === null || this.selectedPinIndex < 0) {
      return null;
    }

    return this.pinBinding();
  }

  /** The absolute path of the selected pin's source item. */
  private selectedPinPrefix(): string {
    const binding = this.selectedPinBinding();

    if (!binding) {
      return '';
    }

    return binding.ListBinding
      ? binding.ListBinding + '[' + this.selectedPinIndex + ']'
      : binding.ObjectBinding;
  }

  /** A settings list arrives as a plain array, or as the Newtonsoft {$type, $values} wrapper. */
  private asArray(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }

    return value && Array.isArray(value.$values) ? value.$values : [];
  }

  private popupPartBinding(part: 'name' | 'value' | 'dataType'): string {
    const binding = this.selectedPinBinding();

    if (!binding) {
      return '';
    }

    if (part === 'name') {
      return binding.PopupNameBinding;
    }

    return part === 'value' ? binding.PopupValueBinding : binding.PopupDataTypeBinding;
  }

  private selectedPopupFieldsPath(): string {
    const binding = this.selectedPinBinding();

    if (!binding || !binding.PopupFieldsBinding) {
      return '';
    }

    return this.combineBinding(this.selectedPinPrefix(), binding.PopupFieldsBinding);
  }

  private savePopupFields(path: string, fields: any[], action: string) {
    const binding = this.selectedPinBinding();
    const sortOrderBinding = binding ? binding.PopupSortOrderBinding : '';
    const orderedFields = fields.map((field, index) => {
      const copy = { ...field };

      if (sortOrderBinding) {
        this.dfms.assign(copy, sortOrderBinding, index + 1, true);
      }

      return copy;
    });

    this.assignBoundValue(path, orderedFields);
    this.notifyPopupFieldsChanged(path, action);
    this.refresh(false);
  }

  private notifyPopupFieldsChanged(path: string, action: string) {
    this.dfms.valueChanged({ binding: path, action: action });
  }

  private assignBoundValue(binding: string, value: any) {
    if (!binding) {
      return;
    }

    const keys = binding.replace(/\[(\d+)\]/g, '.$1').replace(/^\./, '').split('.');
    let target = this.model;

    for (let index = 0; index < keys.length - 1; index++) {
      const key = keys[index];
      const nextKey = keys[index + 1];

      if (target[key] === null || typeof target[key] === 'undefined') {
        target[key] = /^\d+$/.test(nextKey) ? [] : {};
      }

      target = target[key];
    }

    target[keys[keys.length - 1]] = value;
  }

  private isModelLoading(): boolean {
    const keys = Object.keys(this.model || {});
    return keys.length === 1 && keys[0] === 'SerializationObjectFormLink';
  }

  /** A line through the pins in list order, each segment labelled with its length. */
  private renderConnectingLine(positions: L.LatLng[]) {
    for (let i = 1; i < positions.length; i++) {
      this.addSegment(positions[i - 1], positions[i]);
    }
  }

  /**
   * One leg of the line, drawn the way a map draws a route: a white casing under a coloured core, so
   * a 3px line stays readable over pale fields and dark forest alike. The casing is also what the
   * pointer hits - it is a painted stroke four times the width of the core, which turns a hairline
   * that had to be caught exactly into a comfortable target.
   */
  private addSegment(from: L.LatLng, to: L.LatLng) {
    const casing = L.polyline([from, to], {
      className: 'sem-geo-map-line-casing',
      color: '#ffffff',
      weight: 13,
      opacity: 0.55,
      lineCap: 'round',
      lineJoin: 'round'
    });

    const core = L.polyline([from, to], {
      className: 'sem-geo-map-line-core',
      color: this.pinColor(),
      weight: 3,
      lineCap: 'round',
      lineJoin: 'round',
      // The casing carries the pointer for both, so the two never fight over a hover.
      interactive: false
    });

    casing.bindTooltip(this.distanceReadout(haversineMeters(from.lat, from.lng, to.lat, to.lng)), {
      className: 'sem-geo-map-distance',
      sticky: true,
      direction: 'top',
      offset: L.point(0, -10),
      opacity: 1
    });

    // Leaflet takes a bound tooltip out of the document the moment the pointer leaves, which leaves
    // nothing to fade. Dropping its mouseout handler hands the close over to the one below.
    casing.off('mouseout');

    let fadeTimer: number = null;

    casing.on('mouseover', () => {
      window.clearTimeout(fadeTimer);
      this.setTooltipOpacity(casing, '1');

      casing.setStyle({ opacity: 0.92 });
      core.setStyle({ weight: 4.5 });
      casing.bringToFront();
      core.bringToFront();
    });

    casing.on('mouseout', () => {
      casing.setStyle({ opacity: 0.55 });
      core.setStyle({ weight: 3 });

      if (!this.setTooltipOpacity(casing, '0')) {
        casing.closeTooltip();
        return;
      }

      // Held open until the fade has played, then closed for real - a tooltip left in the document
      // would keep answering to the pointer.
      fadeTimer = window.setTimeout(() => casing.closeTooltip(), DISTANCE_FADE_MS);
    });

    casing.addTo(this.lineLayer);
    core.addTo(this.lineLayer);
  }

  /**
   * Sets the tooltip's opacity inline, which is where Leaflet keeps it too - a class would lose to
   * its inline style. Returns false when there is no tooltip on screen to fade.
   */
  private setTooltipOpacity(layer: L.Polyline, opacity: string): boolean {
    const tooltip = layer.getTooltip();
    const element = tooltip ? tooltip.getElement() : null;

    if (!element) {
      return false;
    }

    element.style.opacity = opacity;

    return true;
  }

  /** The measurement, set like the coordinate footer: the number carries, the unit stays quiet. */
  private distanceReadout(meters: number): string {
    const kilometres = meters >= 1000;
    const value = kilometres ? (meters / 1000).toFixed(2) : String(Math.round(meters));

    return '<span class="sem-geo-map-distance-value">' + value + '</span>' +
      '<span class="sem-geo-map-distance-unit">' + (kilometres ? 'km' : 'm') + '</span>';
  }

  // ----------------------------------------------------------------- shapes

  /** Draws the shapes the bound property carries. Geometry is supplied by the module, never drawn here. */
  private renderShapes() {
    this.shapeLayer.clearLayers();

    const binding = this.settings.ShapeBinding;

    if (!binding.GeometryBinding) {
      console.error('sem-geo-map: shape binding requires GeometryBinding.');
      return;
    }

    const root = binding.ListBinding || binding.ObjectBinding;

    if (binding.ListBinding) {
      const items = this.getBoundList(binding.ListBinding);

      if (items === null) {
        if (!this.isModelLoading()) {
          console.error('sem-geo-map: shape list binding ' + root + ' must resolve to a list.');
        }
        return;
      }

      this.drawShapeItems(items, root, binding);
      return;
    }

    // Nothing named: the mappings are read off the form model itself, so there is one shape to draw.
    const source = root ? this.dfms.getValue(this.model, root) : this.model;

    this.drawShapeItems([source], root, binding);
  }

  private drawShapeItems(items: any[], root: string, binding: GeoMapShapeBinding) {
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }

    items.forEach((_, index) => {
      const prefix = binding.ListBinding ? root + '[' + index + ']' : root;
      const geometry = this.parseGeoJson(this.valueAtBinding(prefix, binding.GeometryBinding));

      if (!geometry) {
        console.warn(
          'sem-geo-map: skipped invalid geometry at ' +
          this.combineBinding(prefix, binding.GeometryBinding) + '.'
        );
        return;
      }

      const label = this.valueAtBinding(prefix, binding.LabelBinding);
      const highlighted = !!this.valueAtBinding(prefix, binding.HighlightedBinding);

      this.drawGeoJson(geometry, highlighted, label, this.popupContentAt(prefix, binding));
    });
  }

  private drawGeoJson(
    geometry: any,
    highlighted: boolean = false,
    label: any = null,
    popup: GeoMapPopupContent = null
  ) {
    let layer: L.GeoJSON;

    // Highlighting reuses the platform's own "needs attention" amber ($statusIdle) rather than a
    // colour of this control's own.
    const shapeColor = this.pinColor();
    const highlightColor = '#f7b422';

    const baseStyle = (): L.PathOptions => ({
      color: highlighted ? highlightColor : shapeColor,
      weight: highlighted ? 3.5 : 3,
      fillColor: highlighted ? highlightColor : shapeColor,
      fillOpacity: highlighted ? 0.32 : 0.22
    });

    // The pointer's own feedback, layered over whichever base colour the "Highlighted" binding
    // already picked - so a data-highlighted parcel still visibly reacts to the cursor.
    const hoverStyle = (): L.PathOptions => ({
      ...baseStyle(),
      weight: highlighted ? 4.5 : 4,
      fillOpacity: highlighted ? 0.42 : 0.34
    });

    try {
      layer = L.geoJSON(geometry, {
        style: baseStyle,
        pointToLayer: (feature, latlng) => L.marker(latlng, { icon: this.buildIcon() }),
        // Called once per drawn path (a MultiPolygon/GeometryCollection can yield more than one), so
        // hover feedback and the click-to-popup binding below both reach every ring the item draws.
        onEachFeature: (feature, featureLayer) => {
          if (!(featureLayer instanceof L.Path)) {
            return;
          }

          featureLayer.on('mouseover', () => {
            featureLayer.setStyle(hoverStyle());
            featureLayer.bringToFront();
          });

          featureLayer.on('mouseout', () => {
            featureLayer.setStyle(baseStyle());
          });
        }
      });
    } catch (e) {
      console.error('sem-geo-map: cannot render geometry', e);
      this.dataError = this.translateOr(
        'WebGalis_GeoMap_InvalidGeometry',
        'Geometrije ni bilo mogoče prikazati.'
      );

      return;
    }

    // No coordinate footer: a parcel covers ground rather than standing at a point, so a single
    // reading would say less than the outline already shows.
    const popupHtml = this.buildPopupHtml(popup);

    if (popupHtml) {
      layer.bindPopup(popupHtml, this.popupOptions());
    }

    layer.addTo(this.shapeLayer);

    const bounds = layer.getBounds();

    if (bounds.isValid()) {
      this.extendBounds(bounds);

      if (label) {
        L.marker(bounds.getCenter(), {
          interactive: false,
          icon: L.divIcon({
            className: 'sem-geo-map-shape-label',
            // Sized to nothing so the anchor lands exactly on the centroid; the inner span carries the
            // text and centres itself on that point, which Leaflet's own transform cannot be asked to do.
            iconSize: [0, 0],
            html: '<span>' + this.escapeHtml(label) + '</span>'
          })
        }).addTo(this.shapeLayer);
      }
    }
  }

  // ----------------------------------------------------------------- markers

  private addMarker(position: L.LatLng, pin: GeoMapPin, index: number) {
    // Markers are always created draggable so the handler exists; applyEditability disables it while
    // the form is read only and re-enables it the moment edit mode is switched on.
    const marker = L.marker(position, { icon: this.buildIcon(), draggable: true });

    const rendered: GeoMapMarker = {
      marker: marker,
      latitudeBinding: pin.Latitude,
      longitudeBinding: pin.Longitude,
      sourceIndex: index
    };

    const popupHtml = this.buildPopupHtml(pin.Popup, position);

    if (popupHtml) {
      marker.bindPopup(popupHtml, this.popupOptions());
    }

    marker.on('dragend', () => this.onMarkerMoved(rendered, marker.getLatLng()));
    marker.on('contextmenu', () => this.removePin(rendered.sourceIndex));
    marker.on('click', (event: L.LeafletMouseEvent) => {
      if (!this.isEditable()) {
        return;
      }

      L.DomEvent.stopPropagation(event.originalEvent);
      this.selectPin(rendered.sourceIndex);
      marker.closePopup();
    });

    marker.addTo(this.markerLayer);
    this.renderedMarkers.push(rendered);
  }

  private onMarkerMoved(rendered: GeoMapMarker, position: L.LatLng) {
    if (!this.isEditable()) {
      return;
    }

    const converted = fromWgs84(position.lat, position.lng, this.settings.CoordinateSystem);
    // Metres for a projected grid, roughly a centimetre for degrees.
    const decimals = this.settings.CoordinateSystem === 'D96Tm' ? 2 : 7;

    this.writeCoordinates(
      { latitude: rendered.latitudeBinding, longitude: rendered.longitudeBinding },
      this.round(converted.lat, decimals),
      this.round(converted.lng, decimals),
      'move-map-pin'
    );
  }

  /**
   * Writes a position to the two mapped coordinate properties and redraws in place.
   *
   * DynamicFieldsManagerService.assign splits a path on dots alone, so an item path like
   * `MapPoints[0].Latitude` would land on a property of that literal name instead of in the list -
   * hence assignBoundValue, which resolves the index, plus an explicit change notification.
   */
  private writeCoordinates(
    paths: { latitude: string; longitude: string },
    latitude: number,
    longitude: number,
    action: string
  ) {
    this.assignBoundValue(paths.latitude, latitude);
    this.assignBoundValue(paths.longitude, longitude);
    this.dfms.valueChanged({ binding: this.pinRoot(), action: action });
    this.refresh(false);
  }

  private addPinAt(position: L.LatLng) {
    const binding = this.settings.MultiPinBinding;

    if (!this.isEditable()) {
      return;
    }

    if (!binding || !binding.ListBinding) {
      this.placeSinglePinAt(position);
      return;
    }

    const items = this.getBoundList(binding.ListBinding);

    if (items === null) {
      console.error('sem-geo-map: cannot add a pin because ' + binding.ListBinding + ' is not a list.');
      return;
    }

    if (!this.canCreatePins(binding)) {
      console.error(
        'sem-geo-map: cannot create a pin from a nested coordinate mapping (' +
        binding.LatitudeBinding + ' / ' + binding.LongitudeBinding + ') on ' + binding.ListBinding +
        '. Create the record through the form; the map positions the ones that already exist.'
      );
      return;
    }

    const converted = fromWgs84(position.lat, position.lng, this.settings.CoordinateSystem);
    const decimals = this.settings.CoordinateSystem === 'D96Tm' ? 2 : 7;
    const item: any = {};

    this.dfms.assign(item, binding.LatitudeBinding, this.round(converted.lat, decimals), true);
    this.dfms.assign(item, binding.LongitudeBinding, this.round(converted.lng, decimals), true);

    if (binding.PopupFieldsBinding) {
      this.dfms.assign(item, binding.PopupFieldsBinding, [], true);
    }

    const nextItems = items.concat([item]);
    this.dfms.assign(this.model, binding.ListBinding, nextItems, true);
    this.dfms.valueChanged({ binding: binding.ListBinding, action: 'add-map-pin' });
    this.refresh(false);
  }

  /**
   * A single-pin record without coordinates yet has no marker to drag, so the first click in edit mode
   * places it. Once the pin exists a click leaves it alone and the drag moves it, as before. The
   * coordinates must sit on the form model or on an object that is already there: the map does not
   * create the related record a nested ObjectBinding points at (same rule as canCreatePins).
   */
  private placeSinglePinAt(position: L.LatLng) {
    const binding = this.pinBinding();

    if (!binding || binding.ListBinding || !binding.LatitudeBinding || !binding.LongitudeBinding) {
      return;
    }

    if (!this.canCreatePins(binding)) {
      return;
    }

    const owner = binding.ObjectBinding ? this.dfms.getValue(this.model, binding.ObjectBinding) : this.model;

    if (!owner || typeof owner !== 'object') {
      return;
    }

    const latitude = this.combineBinding(binding.ObjectBinding, binding.LatitudeBinding);
    const longitude = this.combineBinding(binding.ObjectBinding, binding.LongitudeBinding);
    const current = toWgs84(
      this.dfms.getValue(this.model, latitude),
      this.dfms.getValue(this.model, longitude),
      this.settings.CoordinateSystem
    );

    if (current && this.isRenderable(current.lat, current.lng)) {
      return;
    }

    const converted = fromWgs84(position.lat, position.lng, this.settings.CoordinateSystem);
    const decimals = this.settings.CoordinateSystem === 'D96Tm' ? 2 : 7;

    this.writeCoordinates(
      { latitude: latitude, longitude: longitude },
      this.round(converted.lat, decimals),
      this.round(converted.lng, decimals),
      'add-map-pin'
    );
  }

  private removePin(index: number) {
    const binding = this.settings.MultiPinBinding;

    if (!this.isEditable() || !binding || !binding.ListBinding) {
      return;
    }

    const items = this.getBoundList(binding.ListBinding);

    if (items === null || index < 0 || index >= items.length) {
      console.error('sem-geo-map: cannot remove pin ' + index + ' from ' + binding.ListBinding + '.');
      return;
    }

    this.dfms.assign(this.model, binding.ListBinding, items.filter((_, itemIndex) => itemIndex !== index), true);
    this.dfms.valueChanged({ binding: binding.ListBinding, action: 'remove-map-pin' });
    this.refresh(false);
  }

  /**
   * The platform accent, read off the host so the palette stays defined in one place
   * (configuration/variables.scss -> --wg-accent). Resolved once; it does not change at runtime.
   */
  private accentColor(): string {
    if (this.resolvedAccent === null) {
      const styles = this.control && this.control.nativeElement
        ? getComputedStyle(this.control.nativeElement)
        : null;

      const value = styles ? styles.getPropertyValue('--wg-accent') : '';
      this.resolvedAccent = (value || '').trim() || '#3b82f6';
    }

    return this.resolvedAccent;
  }

  /** Pins and lines follow the accent unless the form pins a colour down in ShapeColor. */
  private pinColor(): string {
    return this.settings.ShapeColor || this.accentColor();
  }

  private buildIcon(): L.DivIcon {
    const color = this.pinColor();
    const centre =
      '<circle cx="15" cy="14" r="6" fill="#ffffff"/><circle cx="15" cy="14" r="2.5" fill="' + color + '"/>';

    const svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42">' +
      '<path d="M15 1C7.3 1 1 7.2 1 14.8 1 25.2 15 41 15 41s14-15.8 14-26.2C29 7.2 22.7 1 15 1z" ' +
      'fill="' + color + '" stroke="#ffffff" stroke-width="2"/>' +
      centre +
      '</svg>';

    return L.divIcon({
      html: svg,
      className: 'sem-geo-map-pin',
      iconSize: [30, 42],
      iconAnchor: [15, 42],
      popupAnchor: [0, -38]
    });
  }

  // ------------------------------------------------------------------ popups

  /**
   * Popup rows are rendered the way SemOneToManyContainer renders its listing fields: the mapped name
   * labels the row, the mapped value fills it and the mapped data type drives the formatting.
   */
  private buildPopupHtml(content: GeoMapPopupContent, position: L.LatLng = null): string {
    const rows: string[] = [];

    if (content && content.title) {
      rows.push('<div class="sem-geo-map-popup-title">' + this.escapeHtml(content.title) + '</div>');
    }

    if (content && content.description) {
      rows.push(
        '<div class="sem-geo-map-popup-note">' + this.escapeHtml(content.description) + '</div>'
      );
    }

    (content && content.rows ? content.rows : []).forEach(field => {
      const value = this.formatValue(field.value, field.dataType);

      if (value === '') {
        return;
      }

      rows.push(
        '<div class="sem-geo-map-popup-row">' +
          '<span class="sem-geo-map-popup-label">' +
          this.escapeHtml(field.name || '') +
          '</span>' +
          '<span class="sem-geo-map-popup-value">' +
          this.escapeHtml(value) +
          '</span>' +
          '</div>'
      );
    });

    const fix = this.buildCoordinateFix(position);

    if (rows.length === 0 && !fix) {
      return '';
    }

    return '<div class="sem-geo-map-popup">' + rows.join('') + fix + '</div>';
  }

  private popupOptions(): L.PopupOptions {
    return {
      className: 'sem-geo-map-bubble',
      maxWidth: 300,
      minWidth: 208,
      autoPanPadding: L.point(16, 16)
    };
  }

  /**
   * The bubble's footer: the pin's position, in the same system the record stores it in, under the
   * name of that system. A cadastral record is its coordinate, so the bubble states it rather than
   * leaving it to whichever text boxes the form happens to bind - and naming the grid keeps a D96/TM
   * northing from being read as a latitude.
   */
  private buildCoordinateFix(position: L.LatLng): string {
    if (!position) {
      return '';
    }

    const d96 = this.settings.CoordinateSystem === 'D96Tm';
    const converted = fromWgs84(position.lat, position.lng, this.settings.CoordinateSystem);
    const decimals = d96 ? 2 : 6;

    // fromWgs84 keeps the binding order: for D96/TM `lat` carries the northing, `lng` the easting.
    const axes = d96
      ? [{ name: 'N', value: converted.lat }, { name: 'E', value: converted.lng }]
      : [{ name: 'Lat', value: converted.lat }, { name: 'Lon', value: converted.lng }];

    const values = axes.map(axis =>
      '<span><span class="sem-geo-map-popup-axis">' + axis.name + '</span>' +
      this.escapeHtml(this.formatFix(axis.value, decimals)) + (d96 ? '' : '&deg;') +
      '</span>'
    );

    return '<div class="sem-geo-map-popup-fix">' +
      '<span class="sem-geo-map-popup-crs">' + (d96 ? 'D96/TM' : 'WGS 84') + '</span>' +
      '<span class="sem-geo-map-popup-fix-values">' + values.join('') + '</span>' +
      '</div>';
  }

  private formatFix(value: number, decimals: number): string {
    if (!isFinite(value)) {
      return '-';
    }

    // Slovenian is registered globally in app.module, so the separators match every other number
    // the platform prints.
    return value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  private formatValue(value: any, dataType: string): string {
    if (value === null || typeof value === 'undefined' || value === '') {
      return '';
    }

    const type = (dataType || '').toLowerCase();

    if (type === 'date' || type === 'datetime') {
      const date = new Date(value);

      if (isNaN(date.getTime())) {
        return String(value);
      }

      return type === 'datetime' ? date.toLocaleString() : date.toLocaleDateString();
    }

    if (type === 'bool' || type === 'boolean') {
      return value
        ? this.translateOr('WebGalis_Yes', 'Da')
        : this.translateOr('WebGalis_No', 'Ne');
    }

    if (
      type === 'number' ||
      type === 'double' ||
      type === 'decimal' ||
      type === 'int' ||
      type === 'integer'
    ) {
      const parsed = toNumber(value);

      return parsed === null ? String(value) : parsed.toLocaleString();
    }

    if (typeof value === 'object') {
      // Related documents are shown by their title, as everywhere else in the platform.
      return value.Title ? String(value.Title) : '';
    }

    return String(value);
  }

  private escapeHtml(value: any): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private translateOr(key: string, fallback: string): string {
    if (!key) {
      return fallback;
    }

    const translated = this.translationsService.translate(key);

    return !translated || translated === key ? fallback : translated;
  }

  // ----------------------------------------------------------------- helpers

  private parseGeoJson(raw: any): any {
    if (!raw) {
      return null;
    }

    let geometry = raw;

    if (typeof raw === 'string') {
      if (raw.trim().length === 0) {
        return null;
      }

      try {
        geometry = JSON.parse(raw);
      } catch (e) {
        console.error('sem-geo-map: geometry is not valid json', e);
        this.dataError = this.translateOr(
          'WebGalis_GeoMap_InvalidGeometry',
          'Geometrije ni bilo mogoče prikazati.'
        );

        return null;
      }
    }

    return geometry && geometry.type ? geometry : null;
  }

  private isRenderable(lat: number, lng: number): boolean {
    return (
      isFinite(lat) &&
      isFinite(lng) &&
      Math.abs(lat) <= 90 &&
      Math.abs(lng) <= 180 &&
      !(lat === 0 && lng === 0)
    );
  }

  private fitToDefault() {
    if (!this.fitOnRender) {
      return;
    }

    this.map.setView([DEFAULT_LATITUDE, DEFAULT_LONGITUDE], this.settings.DefaultZoom);
  }

  private round(value: number, decimals: number): number {
    const factor = Math.pow(10, decimals);

    return Math.round(value * factor) / factor;
  }
}
